import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  useColorModeValue,
  useToast,
  Switch,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { CalendarIcon, CheckCircleIcon, InfoIcon } from '@chakra-ui/icons';
import { FiLink2, FiX, FiRefreshCw } from 'react-icons/fi';
import { Icon } from '@chakra-ui/react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { useCalendarSync } from './useCalendarSync';

/**
 * Calendar Sync Page - Google Calendar integration and synchronization
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function CalendarSyncPage() {
  const { currentUser } = useAuth();
  const {
    loading,
    syncing,
    settings,
    syncedCount,
    lastSyncTime,
    toggleSync,
    disconnect,
    syncAllItems,
  } = useCalendarSync();

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const syncBoxBgColor = useColorModeValue('gray.50', 'gray.900');

  useEffect(() => {
    document.title = 'Calendar Sync - EOS';
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      if (!currentUser?.uid) return;

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      if (error) {
        setMessage({ type: 'error', text: 'Failed to connect Google Calendar. Please try again.' });
        return;
      }

      if (code) {
        // OAuth callback - exchange code for token via cloud function
        try {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const { db } = await import('../../../utils/init-firebase');
          
          const functions = getFunctions(db.app);
          const handleOAuthFunction = httpsCallable(functions, 'handleGoogleCalendarOAuth');
          
          await handleOAuthFunction({ code, userId: currentUser.uid });
          
          setMessage({ type: 'success', text: 'Successfully connected to Google Calendar!' });
          
          // Reload settings
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } catch (error: any) {
          console.error('Error handling OAuth callback:', error);
          setMessage({ 
            type: 'error', 
            text: error?.message || 'Failed to connect Google Calendar. Please try again.' 
          });
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    handleOAuthCallback();
  }, [currentUser]);

  const handleConnect = async () => {
    try {
      // Google OAuth flow
      // This would typically redirect to Google OAuth consent screen
      // For now, we'll use a placeholder
      const CLIENT_ID = process.env.REACT_APP_GOOGLE_CALENDAR_CLIENT_ID;
      const REDIRECT_URI = `${window.location.origin}/eos/calendar-sync`;
      const SCOPE = 'https://www.googleapis.com/auth/calendar.events';
      const RESPONSE_TYPE = 'code';
      const ACCESS_TYPE = 'offline';
      const PROMPT = 'consent';

      if (!CLIENT_ID) {
        setMessage({
          type: 'error',
          text: 'Google Calendar client ID not configured. Please contact your administrator.',
        });
        return;
      }

      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(
        REDIRECT_URI
      )}&response_type=${RESPONSE_TYPE}&scope=${encodeURIComponent(SCOPE)}&access_type=${ACCESS_TYPE}&prompt=${PROMPT}`;

      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      setMessage({ type: 'error', text: 'Failed to start connection process.' });
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Calendar? This will stop syncing your L10 meetings.')) {
      return;
    }

    const success = await disconnect();
    if (success) {
      setMessage({ type: 'success', text: 'Successfully disconnected from Google Calendar.' });
      toast({
        title: 'Disconnected',
        description: 'Google Calendar has been disconnected.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setMessage({ type: 'error', text: 'Failed to disconnect. Please try again.' });
    }
  };

  const handleToggleSync = async () => {
    try {
      await toggleSync();
      const newState = settings?.syncEnabled ? 'disabled' : 'enabled';
      setMessage({
        type: 'success',
        text: `Calendar sync ${newState}`,
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update sync settings.' });
    }
  };

  const handleSyncAll = async () => {
    setMessage(null);
    const result = await syncAllItems();

    if (result.success) {
      setMessage({
        type: 'success',
        text: `Successfully synced ${result.synced} items. ${result.failed > 0 ? `Failed: ${result.failed}` : ''}`,
      });
    } else {
      setMessage({
        type: 'error',
        text: result.errors[0] || 'Failed to sync items',
      });
    }
  };

  const formatLastSync = (timestamp: string | null): string => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  const isConnected = settings !== null;
  const isTokenValid = settings?.googleTokenExpiresAt
    ? (() => {
        const expiresAt = settings.googleTokenExpiresAt;
        // Handle Timestamp, Date, or string
        let expiryDate: Date;
        if (expiresAt instanceof Date) {
          expiryDate = expiresAt;
        } else if (expiresAt instanceof Timestamp) {
          expiryDate = expiresAt.toDate();
        } else if (typeof expiresAt === 'string') {
          expiryDate = new Date(expiresAt);
        } else {
          // Fallback for any other type
          expiryDate = new Date(expiresAt as any);
        }
        return expiryDate > new Date();
      })()
    : false;

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex align="center" gap={3}>
          <Icon as={CalendarIcon} w={8} h={8} color="blue.500" />
          <Box>
            <Heading size="lg" color={headingColor}>
              Google Calendar Sync
            </Heading>
            <Text color={subheadingColor} mt={1}>
              Sync your L10 meetings, milestones, to-dos, and action items to Google Calendar
            </Text>
          </Box>
        </Flex>

        {/* Message Alert */}
        {message && (
          <Alert
            status={message.type}
            borderRadius="md"
            borderLeftWidth="4px"
            borderLeftColor={message.type === 'success' ? 'green.500' : 'red.500'}
          >
            <AlertIcon />
            {message.text}
          </Alert>
        )}

        {/* Main Card */}
        <Box bg={bgColor} borderRadius="md" p={6} borderWidth="1px" borderColor={borderColor}>
          <VStack spacing={6} align="stretch">
            {/* Connection Status */}
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={3}>
                <Box
                  w={3}
                  h={3}
                  borderRadius="full"
                  bg={isConnected && isTokenValid ? 'green.500' : 'gray.300'}
                />
                <Box>
                  <Heading size="sm" color={headingColor}>
                    Connection Status
                  </Heading>
                  <Text fontSize="sm" color={subheadingColor} mt={1}>
                    {isConnected
                      ? isTokenValid
                        ? 'Connected to Google Calendar'
                        : 'Connection expired - please reconnect'
                      : 'Not connected to Google Calendar'}
                  </Text>
                </Box>
              </Flex>

              {isConnected ? (
                <Button
                  leftIcon={<Icon as={FiX} />}
                  variant="outline"
                  onClick={handleDisconnect}
                  disabled={loading}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  leftIcon={<Icon as={FiLink2} />}
                  colorScheme="blue"
                  onClick={handleConnect}
                  disabled={loading}
                >
                  Connect Google Calendar
                </Button>
              )}
            </Flex>

            {isConnected && (
              <>
                <Divider />

                {/* Auto-Sync Toggle */}
                <Flex align="center" justify="space-between">
                  <Box>
                    <Heading size="sm" color={headingColor}>
                      Auto-Sync L10 Meetings
                    </Heading>
                    <Text fontSize="sm" color={subheadingColor} mt={1}>
                      Automatically create Google Calendar events when you start an L10 meeting
                    </Text>
                  </Box>
                  <Switch
                    isChecked={settings?.syncEnabled && isTokenValid}
                    onChange={handleToggleSync}
                    isDisabled={loading || !isTokenValid}
                    colorScheme="blue"
                  />
                </Flex>

                <Divider />

                {/* Sync All Items */}
                <Box>
                  <Heading size="sm" color={headingColor} mb={4}>
                    Sync Milestones, To-Dos & Action Items
                  </Heading>
                  <Text fontSize="sm" color={subheadingColor} mb={4}>
                    All your tasks and milestones synced to Google Calendar
                  </Text>

                  <Box bg={syncBoxBgColor} borderRadius="md" p={4}>
                    <VStack spacing={3} align="stretch">
                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color={subheadingColor}>
                          Currently synced items:
                        </Text>
                        <Badge colorScheme="blue" fontSize="md">
                          {syncedCount}
                        </Badge>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color={subheadingColor}>
                          Last sync:
                        </Text>
                        <Text fontSize="sm" color={headingColor}>
                          {formatLastSync(lastSyncTime)}
                        </Text>
                      </Flex>
                      <Button
                        leftIcon={<Icon as={FiRefreshCw} />}
                        colorScheme="blue"
                        onClick={handleSyncAll}
                        disabled={syncing || !settings?.syncEnabled || !isTokenValid}
                        isLoading={syncing}
                        loadingText="Syncing..."
                        w="full"
                      >
                        Sync All Items Now
                      </Button>
                    </VStack>
                  </Box>

                  <Text fontSize="xs" color={subheadingColor} mt={4}>
                    Items are synced as all-day events on their due dates with reminders 1 week and 1 day before.
                  </Text>
                </Box>

                <Divider />

                {/* How it works */}
                <Box>
                  <Heading size="sm" color={headingColor} mb={3}>
                    How it works
                  </Heading>
                  <List spacing={2}>
                    <ListItem>
                      <ListIcon as={CheckCircleIcon} color="green.500" />
                      When you start an L10 meeting, a calendar event is automatically created
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckCircleIcon} color="green.500" />
                      Events include all meeting details and attendees from your team
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckCircleIcon} color="green.500" />
                      Events are created in your primary Google Calendar
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckCircleIcon} color="green.500" />
                      You can edit or delete the events directly in Google Calendar
                    </ListItem>
                  </List>
                </Box>
              </>
            )}

            {!isConnected && (
              <>
                <Divider />
                <Box>
                  <Heading size="sm" color={headingColor} mb={3}>
                    Why connect?
                  </Heading>
                  <List spacing={2}>
                    <ListItem>
                      <ListIcon as={InfoIcon} color="blue.500" />
                      Keep your schedule in sync across all your tools
                    </ListItem>
                    <ListItem>
                      <ListIcon as={InfoIcon} color="blue.500" />
                      Get reminders before your L10 meetings start
                    </ListItem>
                    <ListItem>
                      <ListIcon as={InfoIcon} color="blue.500" />
                      Automatically block time for important team meetings
                    </ListItem>
                  </List>
                </Box>
              </>
            )}
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}

